import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Upload, Trash2, Loader2, Image as ImageIcon, Globe, Check, X, Lock } from "lucide-react";
import Header, { primeSchoolLogo } from "../components/layout/Header";
import { studentService } from "../services/studentService";
import { useSchoolRole, canEditSchoolSettings } from "../hooks/useSchoolRole";

// Accepted logo formats → canonical Content-Type. We key off the file
// extension (not file.type) since an empty/odd MIME makes the presigned PUT
// fail opaquely. SVG is allowed but flagged below (it can carry script).
const EXT_CONTENT_TYPE = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
  svg: "image/svg+xml",
};
const MAX_LOGO_BYTES = 2 * 1024 * 1024; // 2 MB — a logo never needs more.

const Settings = () => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  // School-settings mutations (name, logo) are ADMIN+ on the server. Below
  // that rank the page is read-only: don't offer Save/Upload buttons that
  // would just 403. Fails open on an unknown role (matches the other pages —
  // the server still enforces the real gate).
  const schoolRole = useSchoolRole();
  const canEdit = canEditSchoolSettings(schoolRole);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [schoolName, setSchoolName] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [adminName, setAdminName] = useState("");
  const [logoUrl, setLogoUrl] = useState(null);
  // "Import from website" state.
  const [urlInput, setUrlInput] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [candidate, setCandidate] = useState(null); // { base64Data, contentType, sourceUrl }
  const [savingCandidate, setSavingCandidate] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [settings, stats] = await Promise.all([
          studentService.getSchoolSettings(),
          studentService.getDashboardStats().catch(() => null),
        ]);
        if (cancelled) return;
        setSchoolName(settings.schoolName || "");
        setNameInput(settings.schoolName || "");
        setLogoUrl(settings.logoUrl || null);
        // Prefill "Import from your website" with the URL the Platform-Admin
        // captured when the school was created (stored on the linked directory
        // entry) — so the admin doesn't retype it and logo import is one click.
        setUrlInput(settings.website || "");
        if (stats) setAdminName([stats.adminFirstName, stats.adminLastName].filter(Boolean).join(" "));
      } catch (error) {
        if (!cancelled) toast.error(error.message || t("settings.loadFailed", { defaultValue: "Failed to load settings" }));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const handleFile = async (file) => {
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    const contentType = EXT_CONTENT_TYPE[ext];
    if (!contentType) {
      toast.error(t("settings.logoBadType", { defaultValue: "Use a PNG, JPG, WEBP, or SVG image." }));
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error(t("settings.logoTooBig", { defaultValue: "Logo must be under 2 MB." }));
      return;
    }

    setSaving(true);
    try {
      // Server-side upload persists the logo and returns the updated settings.
      const updated = await studentService.uploadSchoolLogo(file, contentType);
      setLogoUrl(updated.logoUrl || null);
      primeSchoolLogo(updated.logoUrl || null); // refresh the header on next render
      toast.success(t("settings.logoSaved", { defaultValue: "Logo updated" }));
    } catch (error) {
      toast.error(error.message || t("settings.logoFailed", { defaultValue: "Couldn't update the logo" }));
    } finally {
      setSaving(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSaveName = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed || trimmed === schoolName) return;
    setSavingName(true);
    try {
      const updated = await studentService.updateSchoolSettings({ name: trimmed });
      setSchoolName(updated.schoolName || trimmed);
      setNameInput(updated.schoolName || trimmed);
      toast.success(t("settings.nameSaved", { defaultValue: "School name updated" }));
    } catch (error) {
      toast.error(error.message || t("settings.nameFailed", { defaultValue: "Couldn't update the name" }));
    } finally {
      setSavingName(false);
    }
  };

  const handleExtract = async () => {
    const url = urlInput.trim();
    if (!url) return;
    setExtracting(true);
    setCandidate(null);
    try {
      const result = await studentService.extractLogoFromUrl(url);
      setCandidate(result);
    } catch (error) {
      toast.error(error.message || t("settings.extractFailed", { defaultValue: "Couldn't get a logo from that URL" }));
    } finally {
      setExtracting(false);
    }
  };

  const handleUseExtracted = async () => {
    if (!candidate) return;
    setSavingCandidate(true);
    try {
      const updated = await studentService.saveSchoolLogo({
        base64Data: candidate.base64Data,
        contentType: candidate.contentType,
        filename: "website-logo",
      });
      setLogoUrl(updated.logoUrl || null);
      primeSchoolLogo(updated.logoUrl || null);
      setCandidate(null);
      setUrlInput("");
      toast.success(t("settings.logoSaved", { defaultValue: "Logo updated" }));
    } catch (error) {
      toast.error(error.message || t("settings.logoFailed", { defaultValue: "Couldn't update the logo" }));
    } finally {
      setSavingCandidate(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm(t("settings.logoRemoveConfirm", { defaultValue: "Remove the school logo?" }))) return;
    setSaving(true);
    try {
      await studentService.updateSchoolSettings({ logoUrl: null });
      setLogoUrl(null);
      primeSchoolLogo(null); // clear it from the header too
      toast.success(t("settings.logoRemoved", { defaultValue: "Logo removed" }));
    } catch (error) {
      toast.error(error.message || t("settings.logoFailed", { defaultValue: "Couldn't update the logo" }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white min-h-screen font-manrope pb-[100px]">
      <Header schoolName={schoolName} adminName={adminName} />

      <div className="ml-[23px] mt-6 px-8 flex flex-col gap-6 max-w-3xl">
        <div className="flex flex-col gap-0.5 font-['Manrope']">
          <h2 className="text-gray-900 text-2xl font-bold">{t("settings.title", { defaultValue: "Settings" })}</h2>
          <p className="text-gray-500 text-base font-normal">
            {t("settings.subtitle", { defaultValue: "Manage your school's branding." })}
          </p>
        </div>

        {!canEdit ? (
          <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-600">
            <Lock size={14} className="flex-shrink-0 text-gray-400" />
            {t("settings.readOnlyNote", {
              defaultValue: "You can view these settings, but only school admins can change them.",
            })}
          </div>
        ) : null}

        {loading ? (
          <div className="bg-white rounded-xl outline outline-1 outline-blue-100 p-12 flex justify-center items-center">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {/* School name */}
            <div className="bg-white rounded-2xl border border-blue-100 p-6 flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <h3 className="text-gray-900 text-lg font-bold">{t("settings.nameTitle", { defaultValue: "School name" })}</h3>
                <p className="text-sm text-gray-500">
                  {t("settings.nameHelp", { defaultValue: "Shown in your dashboard header and on the certificates you issue." })}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveName();
                  }}
                  maxLength={120}
                  readOnly={!canEdit}
                  className={`flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none ${canEdit ? "focus:border-primary" : "bg-gray-50 text-gray-500"}`}
                />
                {canEdit ? (
                  <button
                    type="button"
                    onClick={handleSaveName}
                    disabled={savingName || !nameInput.trim() || nameInput.trim() === schoolName}
                    className="h-10 px-4 bg-primary hover:bg-blue-600 text-white text-sm font-semibold rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {savingName ? <Loader2 size={16} className="animate-spin" /> : null}
                    {t("settings.saveName", { defaultValue: "Save" })}
                  </button>
                ) : null}
              </div>
            </div>

            {/* School logo */}
            <div className="bg-white rounded-2xl border border-blue-100 p-6 flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <h3 className="text-gray-900 text-lg font-bold">{t("settings.logoTitle", { defaultValue: "School logo" })}</h3>
                <p className="text-sm text-gray-500">
                  {t("settings.logoHelp", {
                    defaultValue: "Shown on the certificates you issue. PNG, JPG, WEBP, or SVG, up to 2 MB.",
                  })}
                </p>
              </div>

              <div className="flex items-center gap-5">
                {/* Preview */}
                <div className="w-28 h-28 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {logoUrl ? (
                    <img src={logoUrl} alt={t("settings.logoAlt", { defaultValue: "School logo" })} className="w-full h-full object-contain" />
                  ) : (
                    <ImageIcon className="w-8 h-8 text-gray-300" />
                  )}
                </div>

                {canEdit ? (
                <div className="flex flex-col gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".png,.jpg,.jpeg,.webp,.svg,image/png,image/jpeg,image/webp,image/svg+xml"
                    className="hidden"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={saving}
                      className="h-10 px-4 bg-primary hover:bg-blue-600 text-white text-sm font-semibold rounded-lg flex items-center gap-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {saving ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                      {logoUrl
                        ? t("settings.replaceLogo", { defaultValue: "Replace logo" })
                        : t("settings.uploadLogo", { defaultValue: "Upload logo" })}
                    </button>
                    {logoUrl && (
                      <button
                        type="button"
                        onClick={handleRemove}
                        disabled={saving}
                        className="h-10 px-4 border border-gray-200 text-gray-700 text-sm font-semibold rounded-lg flex items-center gap-2 hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <Trash2 size={16} />
                        {t("settings.removeLogo", { defaultValue: "Remove" })}
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-400">
                    {t("settings.logoTip", { defaultValue: "A square or wide transparent PNG looks best on certificates." })}
                  </p>
                </div>
                ) : null}
              </div>

              {/* Import from website */}
              {canEdit ? (
              <div className="border-t border-gray-100 pt-4 flex flex-col gap-2">
                <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
                  <Globe size={15} className="text-primary" />
                  {t("settings.importTitle", { defaultValue: "Or import from your website" })}
                </div>
                <p className="text-xs text-gray-500">
                  {t("settings.importHelp", {
                    defaultValue: "Paste your school's website and we'll try to pull its logo. You'll preview it before it's saved.",
                  })}
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="url"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleExtract();
                    }}
                    placeholder={t("settings.importPlaceholder", { defaultValue: "springfieldhigh.edu" })}
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:border-primary"
                  />
                  <button
                    type="button"
                    onClick={handleExtract}
                    disabled={extracting || !urlInput.trim()}
                    className="h-10 px-4 border border-primary text-primary text-sm font-semibold rounded-lg flex items-center gap-2 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {extracting ? <Loader2 size={16} className="animate-spin" /> : <Globe size={16} />}
                    {extracting
                      ? t("settings.fetching", { defaultValue: "Fetching…" })
                      : t("settings.fetchLogo", { defaultValue: "Fetch logo" })}
                  </button>
                </div>

                {/* Candidate preview + confirm */}
                {candidate && (
                  <div className="mt-1 flex items-center gap-4 rounded-xl border border-blue-100 bg-blue-50/40 p-3">
                    <div className="w-16 h-16 rounded-lg border border-gray-200 bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
                      <img
                        src={`data:${candidate.contentType};base64,${candidate.base64Data}`}
                        alt={t("settings.candidateAlt", { defaultValue: "Logo found on your website" })}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {t("settings.candidateFound", { defaultValue: "Found this logo — use it?" })}
                      </p>
                      <p className="text-xs text-gray-400 truncate">{candidate.sourceUrl}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={handleUseExtracted}
                        disabled={savingCandidate}
                        className="h-9 px-3 bg-primary hover:bg-blue-600 text-white text-sm font-semibold rounded-lg flex items-center gap-1.5 transition-colors disabled:opacity-60"
                      >
                        {savingCandidate ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
                        {t("settings.useLogo", { defaultValue: "Use this" })}
                      </button>
                      <button
                        type="button"
                        onClick={() => setCandidate(null)}
                        disabled={savingCandidate}
                        className="h-9 w-9 border border-gray-200 text-gray-500 rounded-lg flex items-center justify-center hover:bg-gray-50 transition-colors disabled:opacity-60"
                        title={t("settings.discard", { defaultValue: "Discard" })}
                      >
                        <X size={15} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Settings;
